<script lang="ts" setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import zxcvbn from 'ts-zxcvbn'

const { t } = useI18n()

const props = defineProps<{
  password?: string
}>()

const emits = defineEmits<{
  (e: 'pw-strength', id: number): void
}>()

const strength = ref(0)

const pwDesc = computed(() => {
  const keys = [
    'password.strength.veryWeak',
    'password.strength.weak',
    'password.strength.fair',
    'password.strength.good',
    'password.strength.strong'
  ] as const
  const key = keys[strength.value] ?? keys[0]
  return t(key)
})

const strengthColor = computed(() => {
  const colors = [
    'var(--strength-red)',
    'var(--strength-orange)',
    'var(--strength-yellow)',
    'var(--strength-lime)',
    'var(--strength-green)'
  ]
  return colors[strength.value] ?? colors[0]
})

watch(
  () => props.password,
  (password) => {
    if (password !== undefined && password.length > 0) {
      const result = zxcvbn(password)
      strength.value = result.score
      emits('pw-strength', strength.value)
    } else {
      strength.value = 0
      emits('pw-strength', 0)
    }
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="props.password" class="pw-strength-meter">
    <div class="strength-track">
      <div
        v-for="i in 5"
        :key="i"
        class="strength-segment"
        :class="{ active: strength >= i - 1 }"
        :style="{ '--segment-color': strengthColor }"
      />
    </div>
    <p class="strength-label" :style="{ color: strengthColor }">
      {{ $t('password.strength.label', { strength: pwDesc }) }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.pw-strength-meter {
  --strength-red: #ef5350;
  --strength-orange: #ff9800;
  --strength-yellow: #fdd835;
  --strength-lime: #9ccc65;
  --strength-green: #66bb6a;

  margin-top: 8px;
}

.strength-track {
  display: flex;
  gap: 4px;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
}

.strength-segment {
  flex: 1;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  transition: background-color 0.2s ease;

  &.active {
    background-color: var(--segment-color);
  }
}

.strength-label {
  margin: 6px 0 0;
  font-size: 0.8rem;
  font-weight: 500;
  opacity: 0.85;
  transition: color 0.2s ease;
}

.v-theme--lightTheme {
  .strength-segment:not(.active) {
    background: rgba(0, 0, 0, 0.08);
  }
}
</style>
